import { useState, useCallback } from 'react'
import { Image, CheckCircle, AlertCircle } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { thumbnailApi } from '../../services/api/thumbnails'
import { useAuth } from '../../hooks/useAuth'

interface ThumbnailUploaderProps {
  modelId: string
  currentThumbnail?: string
  onUploadComplete: () => void
}

export default function ThumbnailUploader({ modelId, currentThumbnail, onUploadComplete }: ThumbnailUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const { token } = useAuth()

  const resizeImage = async (file: File, maxSize: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = document.createElement('img')
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')!
          
          // Calculate dimensions
          let width = img.width
          let height = img.height
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height
              height = maxSize
            }
          }
          
          canvas.width = width
          canvas.height = height
          
          // Draw and convert to blob
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Failed to create blob'))
          }, 'image/jpeg', 0.9)
        }
        img.src = e.target?.result as string
      }
      reader.readAsDataURL(file)
    })
  }

  const uploadThumbnail = async (file: File) => {
    try {
      setUploading(true)
      setError(null)
      setSuccess(false)

      // Create preview
      const objectUrl = URL.createObjectURL(file)
      setPreview(objectUrl)

      // Resize and upload different sizes
      const sizes = [
        { key: '256', maxSize: 256 },
        { key: '512', maxSize: 512 },
        { key: '1024', maxSize: 1024 }
      ]

      await Promise.all(sizes.map(async ({ key, maxSize }) => {
        const resizedBlob = await resizeImage(file, maxSize)
        // Use the new direct upload API
        await thumbnailApi.uploadThumbnail(
          modelId,
          resizedBlob,
          key as '256' | '512' | '1024',
          token || undefined
        )
      }))

      setSuccess(true)
      onUploadComplete()
      
      // Clean up preview after 2 seconds
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl)
        setPreview(null)
        setSuccess(false)
      }, 2000)
    } catch (error) {
      console.error('Upload error:', error)
      setError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      uploadThumbnail(file)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: uploading
  })

  return (
    <div className="w-full">
      <h3 className="text-sm font-medium text-gray-700 mb-2">Model Thumbnail</h3>
      
      <div className="flex gap-4">
        {/* Current thumbnail */}
        {currentThumbnail && !preview && (
          <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100">
            <img
              src={`${import.meta.env.VITE_API_URL}/api/v2/models/${modelId}/thumbnail?size=256`}
              alt="Current thumbnail"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Upload area */}
        <div className="flex-1">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />
            
            {preview ? (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-24 h-24 mx-auto rounded-lg object-cover"
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {isDragActive ? 'Drop image here' : 'Drag & drop or click to upload'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, WebP (max 10MB)
                </p>
              </>
            )}
          </div>

          {/* Status messages */}
          {success && (
            <div className="mt-2 flex items-center text-green-600 text-sm">
              <CheckCircle className="w-4 h-4 mr-1" />
              Thumbnail uploaded successfully
            </div>
          )}

          {error && (
            <div className="mt-2 flex items-center text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}